import type { Bookmark } from "@/types/europafacts";
import type {
  DataInputTransformer,
  Transformer,
  UserFile,
  UserFileTransformer,
} from "@/types/workspace";

/**
 * Gets the display name for a data input transformer (user_file or data_input)
 * This is a unified helper function to be used everywhere we need to get the input data name
 * 
 * @param inputTransformer - The input transformer (user_file or data_input)
 * @param userFiles - Array of user files (for user_file transformers)
 * @param getBookmarkFromTableId - Function to get bookmark from table ID (for data_input transformers)
 * @param currentLanguage - Current language code for translations
 * @returns The display name for the input data, or undefined if not found
 */
export function getInputDataName(
  inputTransformer: UserFileTransformer | DataInputTransformer | undefined,
  userFiles: UserFile[],
  getBookmarkFromTableId: (tableId: string) => Bookmark | undefined,
  currentLanguage: string
): string | undefined {
  if (!inputTransformer) {
    return undefined;
  }

  if (inputTransformer.type === "user_file") {
    // Try to get fileName from transformer or userFiles
    const userFile = userFiles.find((f) => f.id === inputTransformer.file_id);
    return inputTransformer.file_name || userFile?.name;
  } else if (inputTransformer.type === "data_input") {
    // Get fileName from bookmark
    const bookmark = getBookmarkFromTableId(inputTransformer.db_table_id);
    if (bookmark) {
      return (
        bookmark.db_table.table_name_translation[currentLanguage] ||
        bookmark.db_table.table_name_english ||
        bookmark.db_table.table_name
      );
    }
  }

  return undefined;
}

/**
 * Gets the display name for input data from a dataflow's flow definition.
 * This is a convenience function that finds the input transformer and gets its name.
 * 
 * @param flowDefinitionJson - The flow definition JSON array containing transformers
 * @param userFiles - Array of user files (for user_file transformers)
 * @param getBookmarkFromTableId - Function to get bookmark from table ID (for data_input transformers)
 * @param currentLanguage - Current language code for translations
 * @returns The display name for the input data, or undefined if not found
 */
export function getInputDataNameFromFlow(
  flowDefinitionJson: Transformer[] | undefined,
  userFiles: UserFile[],
  getBookmarkFromTableId: (tableId: string) => Bookmark | undefined,
  currentLanguage: string
): string | undefined {
  if (!flowDefinitionJson) {
    return undefined;
  }

  const inputTransformer = flowDefinitionJson.find(
    (t) => t.type === "user_file" || t.type === "data_input"
  );

  return getInputDataName(
    inputTransformer,
    userFiles,
    getBookmarkFromTableId,
    currentLanguage
  );
}

/**
 * Gets the best available display name for a layer, falling back to input data name if needed.
 * This is useful when a layer's fileName might be missing or set to a default value like "Processed Data".
 * 
 * @param layerFileName - The fileName from the layer data (may be missing or "Processed Data")
 * @param flowDefinitionJson - The flow definition JSON array containing transformers
 * @param userFiles - Array of user files (for user_file transformers)
 * @param getBookmarkFromTableId - Function to get bookmark from table ID (for data_input transformers)
 * @param currentLanguage - Current language code for translations
 * @returns The best available display name, or undefined if not found
 */
export function getLayerDisplayName(
  layerFileName: string | undefined,
  flowDefinitionJson: Transformer[] | undefined,
  userFiles: UserFile[],
  getBookmarkFromTableId: (tableId: string) => Bookmark | undefined,
  currentLanguage: string
): string | undefined {
  // If fileName is provided and not a default value, use it
  if (layerFileName && layerFileName !== "Processed Data") {
    return layerFileName;
  }

  // Otherwise, try to get it from the input transformer
  return getInputDataNameFromFlow(
    flowDefinitionJson,
    userFiles,
    getBookmarkFromTableId,
    currentLanguage
  );
}

